/**
 * The table, as a component — so alignment is by construction rather than by hand.
 *
 * Jacky, 16 September: *"please keep the CSS / UI clean i really hate disaligned, multiple row
 * number"*. He was looking at a list whose Committed cell stacked two jobs and two numbers, so no
 * two rows were the same height and the column could not be read down.
 *
 * The rules this encodes, taken from how modern inventory apps are built:
 *
 *   1. **One value per cell.** A quantity that needs explaining is clickable and opens a detail —
 *      it is not stacked into a second line inside the row.
 *   2. **Numbers right, text left.** Quantities are right-aligned on tabular figures so digits line
 *      up down the column; SKUs and codes are monospace.
 *   3. **Fixed column widths.** `table-layout: fixed` with an explicit colgroup, so a long name
 *      truncates itself instead of shoving the numbers sideways.
 *   4. **Hairlines, 2 % zebra, sticky header.** No vertical borders, no shadows, heavier rule under
 *      the header, header stays named while scrolling.
 *   5. **One row height** (40px comfortable / 30px compact), every gap a multiple of 8.
 *   6. **Skeletons while loading, one honest empty state** that offers the next action.
 */
import type {ReactNode} from 'react';
import {cn} from '@aiw/ui';

export type Col = {label: string; width?: number; num?: boolean; mono?: boolean; hint?: string};

export function DataTable({cols, children, footer, density = 'comfortable', className, empty}: {
  cols: Col[]; children: ReactNode; footer?: ReactNode; density?: 'comfortable' | 'compact'; className?: string; empty?: ReactNode;
}) {
  const rowH = density === 'compact' ? 'h-[30px]' : 'h-[40px]';
  return (
    <div className={cn('overflow-hidden rounded-lg border bg-card', className)}>
      <div className="overflow-x-auto">
        <table className="w-full table-fixed border-collapse text-sm">
          <colgroup>{cols.map((c, i) => <col key={i} style={{width: c.width ? `${c.width}px` : undefined}}/>)}</colgroup>
          <thead>
            <tr>
              {cols.map((c, i) => (
                <th key={i} title={c.hint} scope="col"
                  className={cn('sticky top-0 z-[2] h-8 border-b-[1.5px] border-border bg-card px-2.5 text-micro font-semibold uppercase tracking-wider text-muted-foreground',
                    c.num ? 'text-right' : 'text-left')}>
                  <span className="block truncate">{c.label}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className={cn('[&>tr:nth-child(even)]:bg-foreground/[0.02] [&>tr:hover]:bg-accent/60', rowH)}>{children}</tbody>
          {footer && <tfoot className="[&>tr]:border-t-[1.5px] [&>tr]:bg-muted [&>tr]:text-xs">{footer}</tfoot>}
        </table>
      </div>
      {empty}
    </div>
  );
}

/** One cell. `num` right-aligns on tabular figures; `mono` is for codes; children truncate. */
export function Td({children, num, mono, dim, title, className, colSpan}: {
  children?: ReactNode; num?: boolean; mono?: boolean; dim?: boolean; title?: string; className?: string; colSpan?: number;
}) {
  return (
    <td colSpan={colSpan} title={title}
      className={cn('truncate border-b border-border/60 px-2.5 align-middle',
        num && 'text-right font-semibold tabular-nums',
        mono && 'font-mono text-xs tracking-tight',
        dim && 'font-normal text-muted-foreground',
        className)}>
      {children}
    </td>
  );
}

/** A quantity you can click, because the explanation belongs one click down — never in the row. */
export function Quantity({value, onOpen, tone}: {value: number | string; onOpen?: () => void; tone?: 'warn' | 'ok'}) {
  const text = typeof value === 'number' ? value.toLocaleString('en-AU') : value;
  const body = <span className={cn('cursor-pointer border-b border-dashed border-muted-foreground/60 hover:border-primary hover:text-primary',
    tone === 'warn' && 'text-warning', tone === 'ok' && 'text-success')}>{text}</span>;
  return onOpen ? <button type="button" onClick={onOpen} aria-label={`Show the detail for ${text}`}>{body}</button> : body;
}

/** A tag that cannot change width by accident — location codes, categories, statuses. */
export function Tag({children, mono, tone}: {children: ReactNode; mono?: boolean; tone?: 'warn' | 'ok' | 'info'}) {
  return (
    <span className={cn('inline-flex h-5 max-w-full items-center truncate rounded border bg-muted px-1.5 text-micro text-muted-foreground',
      mono && 'font-mono', tone === 'warn' && 'border-warning/40 bg-warning/15 text-warning',
      tone === 'ok' && 'border-success/40 bg-success/15 text-success', tone === 'info' && 'border-primary/30 bg-primary/10 text-primary')}>
      {children}
    </span>
  );
}

/** Skeleton rows, not a spinner: the layout is already the right shape when data lands. */
export function TableSkeleton({cols, rows = 6}: {cols: number; rows?: number}) {
  return <>{Array.from({length: rows}, (_, r) => (
    <tr key={r} className="border-b border-border/60">
      {Array.from({length: cols}, (_, c) => (
        <td key={c} className="px-2.5"><span className="block h-3 animate-pulse rounded bg-muted" style={{width: c === 0 ? '60%' : '80%'}}/></td>
      ))}
    </tr>
  ))}</>;
}
